import type { MotionStyle } from 'framer-motion';
import { AnimatePresence, motion } from 'framer-motion';
import { upperFirst } from 'lodash';
import React, { PropsWithChildren, ReactNode, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { useKey } from 'react-use';

import { ReactComponent as CloseIcon } from '@skippay/core/assets/icons/close.svg';
import { FormattedMessage } from '@skippay/core/i18n/intl';
import { be, bm } from '@skippay/core/utils/classUtils';

import './index.sass';

const MODULE_NAME = 'ModalContent';

type Props = {
	darkCloseCross?: boolean;
	Footer?: ReactNode;
	hasSensitiveContent?: boolean;
	Header?: ReactNode;
	headerNoBorder?: boolean;
	heightAuto?: boolean;
	noSpacing?: boolean;
	className?: string;
	classNameFooter?: string;
	classNameHeader?: string;
	classNameMain?: string;
	closeBtnHandler?: () => void;
	closeBtnToTop?: boolean;
	stickyFooter?: {
		content: JSX.Element;
		scroller: JSX.Element;
	};
	testId: string;
};

function MotionDiv({
	style,
	modalRef,
	children,
}: PropsWithChildren<{
	style?: MotionStyle;
	modalRef: React.RefObject<HTMLElement>;
}>): JSX.Element {
	return (
		<motion.div
			style={style}
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			onClick={() => {
				modalRef.current?.scrollTo({
					top: modalRef.current.scrollHeight,
					behavior: 'smooth',
				});
			}}
		>
			{children}
		</motion.div>
	);
}

function ModalContent(props: PropsWithChildren<Props>): JSX.Element {
	const { ref: stickyFooterRef, inView: stickyFooterContentInView } = useInView();
	const { ref: bottomElement, inView: bottomElementInView } = useInView();
	const modalRef = useRef<HTMLElement>(null);

	useKey(
		'Escape',
		() => {
			props.closeBtnHandler?.();
		},
		{ event: 'keyup' }
	);

	const renderContent = () => (
		<>
			{props.Header}
			{props.closeBtnHandler && (
				<button
					className={be(MODULE_NAME, 'btnClose', {
						toTop: props.closeBtnToTop || !props.Header,
						darkCloseCross: props.darkCloseCross,
					})}
					data-test-id="btnCloseModal"
					onClick={props.closeBtnHandler}
					type="button"
				>
					<span className="visually-hidden">
						<FormattedMessage id="ModalContent.close" defaultMessage="Zavřít" />
					</span>
					<CloseIcon />
				</button>
			)}
		</>
	);

	return (
		<section
			className={bm(MODULE_NAME, { heightAuto: props.heightAuto }, props.className)}
			data-test-id={`modal${upperFirst(props.testId)}`}
			tabIndex={-1}
			aria-label="Modal"
			{...(props.hasSensitiveContent && { 'data-clarity-mask': true })}
		>
			{props.Header ? (
				<header
					className={be(
						MODULE_NAME,
						'header',
						[props.closeBtnHandler && 'hasBtnClose', props.headerNoBorder && 'noBorder'],
						props.classNameHeader
					)}
				>
					{renderContent()}
				</header>
			) : (
				renderContent()
			)}
			<main
				className={be(MODULE_NAME, 'main', [props.noSpacing && 'noSpacing'], props.classNameMain)}
				ref={modalRef}
			>
				<div className={be(MODULE_NAME, 'mainContent')}>
					{props.children}
					<div ref={bottomElement} />
				</div>

				<AnimatePresence>
					{props.stickyFooter && !stickyFooterContentInView && (
						<MotionDiv
							style={{
								position: 'fixed',
								bottom: 0,
								left: 0,
								width: '100%',
							}}
							modalRef={modalRef}
						>
							{props.stickyFooter.scroller}
						</MotionDiv>
					)}
				</AnimatePresence>

				{props.stickyFooter?.content && (
					<div ref={stickyFooterRef}>{props.stickyFooter.content}</div>
				)}
			</main>

			{props.Footer ? (
				<footer className={be(MODULE_NAME, 'footer', null, props.classNameFooter)}>
					<AnimatePresence>
						{!bottomElementInView && (
							<MotionDiv
								style={{
									position: 'absolute',
									bottom: '100%',
									left: 0,
									width: '100%',
								}}
								modalRef={modalRef}
							>
								<div className={be(MODULE_NAME, 'bottomShadow', ['inFooter'])} />
							</MotionDiv>
						)}
					</AnimatePresence>
					<div>{props.Footer}</div>
				</footer>
			) : (
				<AnimatePresence>
					{!bottomElementInView && (
						<MotionDiv
							style={{
								position: 'absolute',
								bottom: '0',
								left: 0,
								width: '100%',
							}}
							modalRef={modalRef}
						>
							<div className={be(MODULE_NAME, 'bottomShadow')} />
						</MotionDiv>
					)}
				</AnimatePresence>
			)}
		</section>
	);
}

export default ModalContent;
